export interface IResultSet {
  src: string;
  thumbnail?: string;
  idx: number;
  imageType?: string | null;
  tag?: string;
  sortNumber?: number | null;
  sortLetter?: string | null;
}

/** Sort and enrich incoming image data */
export function buildSortedImages(images: any[]): IResultSet[] {
  const enriched = images.map((image, idx) => {
    const src = image.imageUrlCf;
    const thumbnail = src ? src.replace(/(\.[^./\?]+)$/, '_thumbnail.jpg') : '';
    const { sortNumber, sortLetter, imageType } = extractSortDetails(src || "");
    return { src, idx, tag: image.imageTag, sortNumber, sortLetter, imageType, thumbnail };
  });

  const imageTypeOrder: string[] = [];
  for (const img of enriched) {
    if (img.imageType && !imageTypeOrder.includes(img.imageType)) {
      imageTypeOrder.push(img.imageType);
    }
  }

  return imageTypeOrder.flatMap((type) => {
    return enriched
      .filter(img => img.imageType === type)
      .sort((a, b) => {
        if ((a.sortNumber ?? 999) !== (b.sortNumber ?? 999)) {
          return (a.sortNumber ?? 999) - (b.sortNumber ?? 999);
        }
        return (a.sortLetter || "").localeCompare(b.sortLetter || "");
      });
  });
}

/** Return unique image types from an array */
export function getUniqueImageTypes(images: IResultSet[]): string[] {
  return Array.from(
    new Set(
      images
        .map((i) => i.imageType)
        .filter((t): t is string => Boolean(t))
    )
  );
}

/** Check if WebGL is supported */
export function isWebGLSupported(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")));
  } catch {
    return false;
  }
}

/** Detect if running inside iframe mode */
export function isIframe(): boolean {
  return window.location.pathname.includes("/iframe/");
}

/** Copy the current URL to clipboard safely */
export async function copyUrl(setCopied: (val: boolean) => void) {
  try {
    const url = window.location.href;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  } catch (err) {
    console.error("Failed to copy:", err);
  }
}

export const extractSortDetails = (
    imageUrl: string
): {
    sortNumber: number | null;
    sortLetter: string | null;
    imageType: string | null;
} => {
    if (!imageUrl) return { sortNumber: null, sortLetter: null, imageType: null };

    const parts = imageUrl.split("/");
    const filename = parts[parts.length - 1] || "";
    const folderName = parts[parts.length - 2] || "";

    // 🧠 Default imageType derived from folder name
    let imageType = folderName || null;

    const filenameParts = filename.split("-");
    const sortNumber = parseInt(filenameParts[1]);
    const sortLetter = filenameParts[2] || null;

    return { sortNumber, sortLetter, imageType };
};
